import { defineStore } from 'pinia'

export type ThemeMode = 'light' | 'dark' | 'system'

/** Must match key in persist and the inline script in index.html. */
const STORAGE_KEY = 'kantiro-theme'

function getSystemDark(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyToDocument(isDark: boolean): void {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle('dark', isDark)
}

export const useThemeStore = defineStore('themeStore', {
  state: (): { theme: ThemeMode } => ({
    theme: 'system',
  }),
  getters: {
    /** Resolved theme: 'light' or 'dark' for applying the class. */
    effectiveTheme(): 'light' | 'dark' {
      if (this.theme === 'light') return 'light'
      if (this.theme === 'dark') return 'dark'
      return getSystemDark() ? 'dark' : 'light'
    },
    isDark(): boolean {
      return this.effectiveTheme === 'dark'
    },
  },
  actions: {
    setTheme(mode: ThemeMode) {
      this.theme = mode
      this.apply()
    },
    /** Apply theme to document; call on init and when system preference changes. */
    apply() {
      applyToDocument(this.effectiveTheme === 'dark')
    },
    /**
     * Init: apply once and listen to prefers-color-scheme when in system mode.
     * Call after Pinia is installed (e.g. in main.ts).
     */
    init() {
      this.apply()
      if (typeof window === 'undefined') return
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      const onChange = () => {
        if (this.theme === 'system') this.apply()
      }
      mq.addEventListener('change', onChange)
      // Store cleanup on app teardown is optional; listener is small.
    },
  },
  persist: {
    storage: localStorage,
    key: STORAGE_KEY,
  },
})
